import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Watches for size changes so the marquee overflow check re-runs on resize.
   * @type {ResizeObserver|undefined}
   */
  #resizeObserver = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();

    // Marquee: scroll any announcement whose text is too long to fit; leave short ones static.
    requestAnimationFrame(() => this.#updateMarquees());
    this.#resizeObserver = new ResizeObserver(() => this.#updateMarquees());
    this.#resizeObserver.observe(this);
    // Custom fonts can change text width after first paint — re-measure once they load.
    document.fonts?.ready?.then(() => this.#updateMarquees());
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.#resizeObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * Enables a seamless marquee only on announcements whose text overflows the bar.
   * Messages that fit stay static and centered. Detection: compare the text's
   * scrollWidth (its full one-line width) against the viewport's clientWidth.
   */
  #updateMarquees() {
    const PIXELS_PER_SECOND = 60;
    const slides = this.querySelectorAll('.announcement-bar__slide');

    slides.forEach((slide) => {
      const viewport = slide.querySelector('.announcement-bar__viewport');
      const track = slide.querySelector('.announcement-bar__track');
      const text = slide.querySelector('.announcement-bar__text');
      if (!viewport || !track || !text) return;

      // Measure the natural width with the marquee OFF.
      slide.classList.remove('is-marquee');
      track.style.removeProperty('--marquee-duration');

      if (text.scrollWidth <= viewport.clientWidth + 1) return;

      // Overflow: turn the marquee ON, then match the duration to a constant speed
      // so short-ish and very long messages both scroll at a readable pace.
      slide.classList.add('is-marquee');
      const unitWidth = text.offsetWidth; // one copy including its trailing gap
      const duration = Math.max(unitWidth / PIXELS_PER_SECOND, 1);
      track.style.setProperty('--marquee-duration', `${duration}s`);
    });
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
